import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { ApiClient, useApiClient } from 'api/context-provider/useApiClient';
import { ApiError, InferFetchData, throwOnError } from 'api/helpers';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

async function getTotpCode(apiClient: ApiClient, accountId: number) {
  return apiClient.GET('/op/v2/account/{id}/totp-code', { params: { path: { id: accountId } } });
}

export default function useTotpCodeQuery(accountId: number, hasTotpSecret: boolean) {
  const apiClient = useApiClient();
  const toast = useToast();
  const { t } = useTranslation();

  const query = useQuery<InferFetchData<typeof getTotpCode>, ApiError>({
    queryKey: getKey(accountId),
    queryFn: () => throwOnError(getTotpCode(apiClient, accountId)),
    enabled: hasTotpSecret,
    // Re-fetch right as the current code expires, instead of polling on a fixed interval, so a fresh code is ready the moment the old one rotates out.
    refetchInterval: (currentQuery) => {
      const totpCode = currentQuery.state.data?.data;
      if (!totpCode) {
        return 2000;
      }
      const expiresAtMs = new Date(totpCode.generated_at).getTime() + totpCode.lifetime_seconds * 1000;
      return Math.max(250, expiresAtMs - Date.now() + 300);
    },
    refetchIntervalInBackground: false,
    retry: false,
  });

  useEffect(() => {
    if (query.isError) {
      toast({ title: query.error.message ?? t('errors:general'), status: 'error' });
    }
  }, [query.error, query.isError, t, toast]);

  return query;
}

function getKey(accountId: number) {
  return ['TotpCode', accountId] as const satisfies QueryKey;
}
useTotpCodeQuery.getKey = getKey;
